'use client';

import { useCallback, useRef } from 'react';
import { flushSync } from 'react-dom';
import { useTheme } from 'next-themes';
import { Moon, Sun } from 'lucide-react';

type StartViewTransition = (callback: () => void) => {
  ready: Promise<void>;
};

export function AnimatedThemeToggler() {
  const { resolvedTheme, setTheme } = useTheme();
  const buttonRef = useRef<HTMLButtonElement>(null);

  const handleClick = useCallback(async () => {
    const next = resolvedTheme === 'dark' ? 'light' : 'dark';
    const docWithVT = document as Document & { startViewTransition?: StartViewTransition };

    if (!docWithVT.startViewTransition || !buttonRef.current) {
      setTheme(next);
      return;
    }

    const transition = docWithVT.startViewTransition(() => {
      flushSync(() => setTheme(next));
    });

    await transition.ready;

    const rect = buttonRef.current.getBoundingClientRect();
    const x = rect.left + rect.width / 2;
    const y = rect.top + rect.height / 2;
    const maxRadius = Math.hypot(
      Math.max(x, window.innerWidth - x),
      Math.max(y, window.innerHeight - y),
    );

    document.documentElement.animate(
      {
        clipPath: [
          `circle(0px at ${x}px ${y}px)`,
          `circle(${maxRadius}px at ${x}px ${y}px)`,
        ],
      },
      {
        duration: 600,
        easing: 'cubic-bezier(0.16, 1, 0.3, 1)',
        pseudoElement: '::view-transition-new(root)',
      },
    );
  }, [resolvedTheme, setTheme]);

  return (
    <button
      ref={buttonRef}
      type="button"
      className="wz-nav-toggle"
      onClick={handleClick}
      aria-label="Toggle theme"
    >
      <Sun size={16} className="wz-nav-toggle-icon wz-nav-toggle-icon--sun" />
      <Moon size={16} className="wz-nav-toggle-icon wz-nav-toggle-icon--moon" />
    </button>
  );
}
